package com.islamicmedia.site.components

import com.islamicmedia.shared.domain.model.filterProfanity
import com.islamicmedia.shared.AppConfig
import androidx.compose.runtime.*
import com.islamicmedia.shared.domain.AccessLink
import com.islamicmedia.shared.domain.AccessLinkKind
import com.islamicmedia.shared.domain.accessLinkFor
import com.islamicmedia.shared.domain.model.AlternativeItem
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.shared.domain.model.Comment
import com.islamicmedia.shared.domain.model.Concern
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.domain.model.TldrPoint
import com.islamicmedia.shared.domain.model.clearConcernCategories
import com.islamicmedia.shared.domain.model.concernDisplayName
import com.islamicmedia.shared.domain.model.verdictLabel
import com.islamicmedia.site.theme.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

private enum class ReviewTab { ISLAMIC, DEVELOPMENTAL }

/** Strips a trailing "(YYYY)" so the rating prompt reads as prose, not a catalogue entry. */
private val trailingYearRe = Regex("""\s*\(\d{4}\)\s*$""")

/** "Where to get it" pill under the verdict. Opens the store/JustWatch in a new tab. */
@Composable
private fun AccessLinkButton(link: AccessLink) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            marginTop(14.px)
            marginBottom(4.px)
        }
    }) {
        Button(attrs = {
            attr("type", "button")
            onClick { kotlinx.browser.window.open(link.url, "_blank") }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "10px")
                padding(14.px, 28.px)
                borderRadius(16.px)
                property("background", GradientPrimary)
                color(Color.white)
                property("border", "none")
                fontSize(15.px)
                fontWeight(700)
                property("letter-spacing", "0.02em")
                cursor("pointer")
                property("font-family", "inherit")
                property("box-shadow", "0 6px 18px rgba(14, 124, 123, 0.35)")
                property("transition", TransitionFast)
            }
        }) {
            AccessIcon(link.kind)
            Text(link.label)
        }
        if (link.isAmazonAffiliate) {
            Div(attrs = {
                style {
                    marginTop(7.px)
                    fontSize(11.px)
                    color(Gray500)
                    property("text-align", "center")
                }
            }) {
                Text("As an Amazon Associate we earn from qualifying purchases.")
            }
        }
    }
}

/** White bag/play icon as a self-contained SVG data-URI background (no external assets). */
@Composable
private fun AccessIcon(kind: AccessLinkKind) {
    val svg = if (kind == AccessLinkKind.SHOP)
        "<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z'/><path d='M3 6h18'/><path d='M16 10a4 4 0 0 1-8 0'/></svg>"
    else
        "<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='%23ffffff'><path d='M8 5v14l11-7z'/></svg>"
    Span(attrs = {
        style {
            property("display", "inline-block")
            width(18.px)
            height(18.px)
            property("flex-shrink", "0")
            property("background-image", "url(\"data:image/svg+xml,$svg\")")
            property("background-repeat", "no-repeat")
            property("background-position", "center")
            property("background-size", "18px 18px")
        }
    })
}

/** Card chrome shared by the below-the-concerns sections (recommendation rows, rating, comments)
 *  so they read as discrete blocks on the verdict sheet like the concern cards above them. */
@Composable
private fun SectionCard(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(rgb(255, 255, 255))
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            border(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.08))
            padding(14.px)
            // Slightly more breathing room than the 8px between concern cards, so these read as a
            // separate group rather than a continuation of the concern list.
            marginTop(12.px)
        }
    }) { content() }
}

/**
 * Expand/collapse chevron for the recommendation rows — points DOWN when collapsed ("there's more
 * below"), UP when expanded. Drawn as an SVG data-URI background like [AccessIcon] rather than
 * innerHTML, which the security hook blocks.
 */
/** Web twin of composeApp's [SafetyShields]: shield-check / shield-caution / shield-x for the
 *  verdict badge. Same Tabler-derived geometry; drawn as an SVG data-URI because the security
 *  hook blocks innerHTML. [hex] is the verdict colour WITHOUT the leading '#'. */
@Composable
private fun SafetyShieldIcon(score: Int, hex: String, sizePx: Int = 18) {
    val outline = "M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -0.09 7.06"
    val d = when {
        // check mark
        score >= 7 -> "$outline M15 19L17 21L21 17"
        // shorter tail + exclamation (the 0.01-long segment renders as a dot under a round cap)
        score >= 4 -> "M12.8 19.9a12 12 0 0 1 -9.3 -13.9a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -0.09 7.06 M17.5 15L17.5 19.2 M17.5 22L17.5 22.01"
        // cross
        else -> "$outline M22 22L17 17 M17 22L22 17"
    }
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' " +
        "fill='none' stroke='%23" + hex + "' stroke-width='2' stroke-linecap='round' " +
        "stroke-linejoin='round'><path d='" + d + "'/></svg>"
    Span(attrs = {
        style {
            property("display", "inline-block")
            width(sizePx.px); height(sizePx.px)
            property("flex-shrink", "0")
            property("background-image", "url(\"data:image/svg+xml,$svg\")")
            property("background-repeat", "no-repeat")
            property("background-position", "center")
            property("background-size", "${sizePx}px ${sizePx}px")
        }
    })
}

@Composable
private fun ChevronToggleIcon(expanded: Boolean) {
    val path = if (expanded) "m18 15-6-6-6 6" else "m6 9 6 6 6-6"
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' " +
        "fill='none' stroke='%23A3A3A3' stroke-width='2.5' stroke-linecap='round' " +
        "stroke-linejoin='round'><path d='$path'/></svg>"
    Span(attrs = {
        style {
            property("display", "inline-block")
            width(20.px)
            height(20.px)
            property("flex-shrink", "0")
            property("background-image", "url(\"data:image/svg+xml,$svg\")")
            property("background-repeat", "no-repeat")
            property("background-position", "center")
            property("background-size", "20px 20px")
        }
    })
}

@Composable
fun CombinedAnalysisResultContent(
    result: AnalysisResult,
    onRunFullAnalysis: (() -> Unit)? = null,
    /** Criteria used for the analysis — required for deriving the retry unit name on custom concerns. */
    criteria: SearchCriteria? = null,
    /** V2: Tap-to-retry callback for failed Phase 2 enrichments. Receives the unit name. */
    onRetryConcern: ((unit: String) -> Unit)? = null,
    /** Dev-only: total duration of this analysis. Renders as a small footer badge. */
    analysisDurationMs: Long? = null,
    /** V2: set of Phase 2 unit names currently being retried. Cards in this set show a spinner. */
    retryingUnits: Set<String> = emptySet(),
    /** True while HIGHLIGHTS are still being generated server-side — shows a skeleton placeholder. */
    tldrLoading: Boolean = false,
    // ── Alternatives / Similar Titles (live-fetched; not part of AnalysisResult) ──
    alternatives: List<AlternativeItem> = emptyList(),
    onAlternativeSelected: (AlternativeItem, Boolean) -> Unit = { _, _ -> },
    islamicAlternatives: List<AlternativeItem> = emptyList(),
    authorAlternatives: List<AlternativeItem> = emptyList(),
    // "Where to get it" button — needs the title + media type (not part of AnalysisResult). Null → no button.
    title: String? = null,
    mediaType: MediaType? = null,
    year: String? = null,
    // ── Comments / Reactions (shared, public — live-fetched; not part of AnalysisResult) ──
    comments: List<Comment> = emptyList(),
    likeCount: Int = 0,
    dislikeCount: Int = 0,
    myReaction: Int? = null,
    isSignedIn: Boolean = true,
    isSubmittingComment: Boolean = false,
    onReact: (Int) -> Unit = {},
    onPostComment: (String) -> Unit = {},
    onEditComment: (id: String, body: String) -> Unit = { _, _ -> },
    onDeleteComment: (id: String) -> Unit = {},
    /** "Flag this analysis" affordance, rendered at the right of the rating row. */
    isFlagged: Boolean = false,
    onFlagClick: (() -> Unit)? = null,
    /** Non-null when a note post/edit/delete failed, shown inline under the composer. */
    commentActionError: String? = null,
    /** Optional extra content under the thumbs row — the admin dashboard passes the list of who
     *  reacted. Null in the public app, so users see no change. */
    reactionDetail: (@Composable () -> Unit)? = null,
) {
    val sortedConcerns = result.concerns.sortedBy {
        if (it.enrichmentStatus == "failed") 99
        else when (it.severity.lowercase()) {
            "high" -> 0
            "medium" -> 1
            else -> 2
        }
    }

    var expandedIndex by remember { mutableStateOf<Int?>(null) }

    // Quick check: hide concerns less severe than the verdict (red verdict → only high,
    // yellow verdict → high+medium, green verdict → all). Full analysis is unfiltered.
    val visibleQuickConcerns = if (result.quickCheck) {
        sortedConcerns.filter { c ->
            val sev = c.severity.lowercase()
            when {
                result.score < 4 -> sev == "high"
                result.score < 7 -> sev == "high" || sev == "medium"
                else -> true
            }
        }
    } else sortedConcerns

    // 1. Score Section with top concerns
    ScoreSection(
        score = result.score,
        recommendation = result.recommendation,
        summary = result.summary,
        ageGuidance = result.ageGuidance,
        topConcerns = if (result.quickCheck) visibleQuickConcerns else sortedConcerns.take(3),
        concernCount = result.concerns.size,
        quickCheck = result.quickCheck,
        onConcernClick = { concern ->
            // The concern cards render while Phase 2 runs too, so the chips always have a card to
            // open. Quick checks have no cards.
            if (result.quickCheck) return@ScoreSection
            val idx = sortedConcerns.indexOfFirst { it.category == concern.category }
            if (idx >= 0) {
                expandedIndex = idx
                kotlinx.browser.window.setTimeout({
                    kotlinx.browser.document.getElementById("concern-$idx")?.scrollIntoView(
                        js("({behavior: 'smooth', block: 'start'})")
                    )
                }, 100)
            }
        }
    )

    // "Where to get it" — under the verdict, on both quick-check and full paths.
    if (title != null && mediaType != null) {
        val accessLink = remember(title, mediaType, result.score, result.author) {
            accessLinkFor(title, mediaType, isGreen = result.score >= 7, author = result.author)
        }
        if (accessLink != null) {
            AccessLinkButton(accessLink)
        }
    }

    // HIGHLIGHTS — shown below the verdict. A skeleton renders while they're still
    // being generated server-side so the section doesn't pop in after everything else.
    val tldr = result.tldr
    if (!tldr.isNullOrEmpty()) {
        TldrSection(tldr, result.score)
    } else if (tldrLoading) {
        TldrSkeleton(result.score)
    }

    // Quick check: stop here and show the upgrade button (no evidence/refs to display).
    if (result.quickCheck) {
        val runFull = onRunFullAnalysis
        if (runFull != null) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    marginTop(20.px)
                    marginBottom(8.px)
                }
            }) {
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        println("[QUICK_CHECK] Run full analysis button clicked")
                        runFull()
                    }
                    style {
                        padding(12.px, 24.px)
                        // Same 14px radius as the section cards. Light powder blue (matches the
                        // theme's Info family on mobile) — secondary action, and it sits in the
                        // app's light palette instead of fighting it.
                        borderRadius(14.px)
                        property("background", "#DBEAFE")
                        property("color", "#2563EB")
                        property("border", "none")
                        fontSize(14.px)
                        fontWeight(700)
                        cursor("pointer")
                        property("font-family", "inherit")
                        property("box-shadow", "0 4px 12px rgba(37, 99, 235, 0.15)")
                        property("transition", TransitionFast)
                    }
                }) {
                    Text(if (result.fullAnalysisAvailable) "Full analysis available" else "Run full analysis")
                }
            }
        }
        AlternativesSection(result.score, alternatives, onAlternativeSelected)
        if ((result.islamicScore ?: 0) < 8) IslamicPicksSection(islamicAlternatives, onAlternativeSelected)
        if (!result.author.isNullOrBlank()) AuthorTitlesSection(result.author!!, authorAlternatives, onAlternativeSelected)
        CommentsSection(comments, likeCount, dislikeCount, myReaction, isSignedIn, isSubmittingComment, onReact, onPostComment, onEditComment, onDeleteComment, isFlagged, onFlagClick, commentActionError, title?.replace(trailingYearRe, "")?.trim(), reactionDetail)
        AnalysisDisclaimer()
        return
    }

    // 2. Expandable concern list
    Div(attrs = {
        style {
            marginBottom(16.px)
        }
    }) {
        Div(attrs = {
            style {
                fontSize(12.px)
                fontWeight(800)
                color(Color.black)
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
                marginBottom(10.px)
            }
        }) { Text("Details") }

        // While Phase 2 runs the cards show anyway: each already has its Phase 1 reason and the
        // moments found, and its full write-up fills in the moment it lands. (Mirrors composeApp's
        // analysisResultLazyItems.)
        sortedConcerns.forEachIndexed { index, concern ->
            val isFailed = concern.enrichmentStatus == "failed"
            val unitName = criteria?.unitNameFor(concern) ?: "concern:${concern.category}"
            val isRetrying = isFailed && unitName in retryingUnits
            val isExpanded = expandedIndex == index && !isFailed
            val (badgeLabel, badgeColor, badgeBg) = when {
                isRetrying -> Triple("Loading…", Gray500, rgba(0, 0, 0, 0.08))
                isFailed -> Triple("Retry", Gray500, rgba(0, 0, 0, 0.08))
                else -> when (concern.severity.lowercase()) {
                    "high" -> Triple("Critical", ScoreRed, rgba(239, 68, 68, 0.12))
                    "medium" -> Triple("Moderate", ScoreAmber, rgba(245, 158, 11, 0.12))
                    else -> Triple("Mild", ScoreGreen, rgba(34, 197, 94, 0.12))
                }
            }
            val cardBg: CSSColorValue = if (isFailed) Gray100 else when (concern.severity.lowercase()) {
                "high" -> rgba(255, 245, 245, 1)
                "medium" -> rgba(255, 252, 240, 1)
                else -> rgba(240, 255, 246, 1)
            }

            Div(attrs = {
                id("concern-$index")
                style {
                    borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
                    backgroundColor(cardBg)
                    padding(12.px)
                    marginBottom(8.px)
                    property("transition", TransitionFast)
                    border(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.08))
                }
            }) {
                // Header row: chevron/refresh + category + badge
                Div(attrs = {
                    onClick {
                        when {
                            isRetrying -> Unit  // ignore clicks while a retry is in flight
                            isFailed -> {
                                val unit = criteria?.unitNameFor(concern) ?: "concern:${concern.category}"
                                onRetryConcern?.invoke(unit)
                            }
                            else -> {
                                val expanding = !isExpanded
                                expandedIndex = if (isExpanded) null else index
                                if (expanding) {
                                    kotlinx.browser.window.setTimeout({
                                        kotlinx.browser.document.getElementById("concern-$index")
                                            ?.scrollIntoView(js("({behavior:'smooth',block:'start'})"))
                                    }, 50)
                                }
                            }
                        }
                    }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("justify-content", "space-between")
                        cursor(if (isRetrying) "wait" else "pointer")
                        // When expanded, pin the concern header at the top of the
                        // scroll area while its details scroll — mirrors mobile's
                        // pinned concern header. Opaque bg so content scrolls under.
                        if (isExpanded) {
                            property("position", "sticky")
                            property("top", "0")
                            property("z-index", "4")
                            backgroundColor(cardBg)
                            paddingTop(6.px)
                            paddingBottom(6.px)
                            property("border-bottom", "1px solid rgba(0,0,0,0.06)")
                        }
                    }
                }) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            property("flex", "1")
                        }
                    }) {
                        Span(attrs = {
                            // Retry icon spins via CSS class while a retry is in flight
                            if (isRetrying) classes("retry-spin")
                            style {
                                fontSize(if (isFailed) 14.px else 10.px)
                                color(if (isFailed) Gray500 else Gray400)
                                fontWeight(if (isFailed) 700 else 400)
                                property("transition", "transform 0.2s ease")
                                if (!isFailed && isExpanded) property("transform", "rotate(90deg)")
                            }
                        }) { Text(if (isFailed) "\u21BB" else "\u25B6") }
                        Span(attrs = {
                            style {
                                fontSize(16.px)
                                fontWeight(700)
                                color(if (isFailed) Gray500 else Gray700)
                            }
                        }) { Text(concernDisplayName(concern.category)) }
                    }
                    Span(attrs = {
                        style {
                            fontSize(10.px)
                            fontWeight(700)
                            color(badgeColor)
                            backgroundColor(badgeBg)
                            borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                            padding(3.px, 10.px)
                            // Don't uppercase "Loading\u2026" \u2014 keep it readable
                            if (!isRetrying) property("text-transform", "uppercase")
                            property("letter-spacing", "0.05em")
                            property("flex-shrink", "0")
                        }
                    }) { Text(badgeLabel) }
                }

                // Failed cards show a retry hint inline; no expansion
                if (isFailed) {
                    Div(attrs = {
                        style {
                            marginTop(8.px)
                            fontSize(12.px)
                            color(Gray500)
                        }
                    }) {
                        Text(
                            if (isRetrying) "Re-running enrichment\u2026"
                            else "Couldn't load details for this concern. Tap to retry."
                        )
                    }
                }

                // Expanded content with tabs
                if (isExpanded) {
                    val hasIslamic = concern.islamicPerspective != null ||
                        concern.quranReference != null || concern.hadithReference != null
                    val hasDevelopmental = concern.developmentalPerspective != null
                    val hasPerspectives = hasIslamic || hasDevelopmental

                    // Description — split into sections so each subsection heading
                    // can pin (position: sticky) just below the concern header
                    // while its body scrolls, mirroring mobile's level-3 pinning.
                    if (concern.description.isNotBlank()) {
                        val sections = remember(concern.description) {
                            splitMarkdownIntoSections(concern.description)
                        }
                        Div(attrs = { style { marginTop(10.px) } }) {
                            if (sections.isEmpty()) {
                                FormattedText(
                                    text = concern.description,
                                    color = Gray600,
                                    headingColor = Gray700,
                                    lineHeight = 1.7.em
                                )
                            } else {
                                sections.forEachIndexed { sIdx, section ->
                                    section.heading?.let { heading ->
                                        if (sIdx > 0) {
                                            Hr(attrs = {
                                                style {
                                                    property("border", "none")
                                                    property("border-top", "0.5px solid $Gray300")
                                                    margin(4.px, 0.px)
                                                }
                                            })
                                        }
                                        Div(attrs = {
                                            style {
                                                // Pin below the concern header (~32px tall).
                                                property("position", "sticky")
                                                property("top", "32px")
                                                property("z-index", "2")
                                                backgroundColor(cardBg)
                                                paddingTop(4.px)
                                                paddingBottom(4.px)
                                                fontSize(13.px)
                                                fontWeight(700)
                                                color(Gray700)
                                                property("line-height", "1.3")
                                            }
                                        }) { Text(heading) }
                                    }
                                    MarkdownBlocks(
                                        blocks = section.body,
                                        color = Gray600,
                                        headingColor = Gray700,
                                        lineHeight = 1.7.em
                                    )
                                }
                            }
                        }
                    }

                    FoundMoments(concern.instances)
                    if (concern.enrichmentStatus == "pending") WriteUpPlaceholder()

                    if (hasPerspectives) {
                        Div(attrs = {
                            style {
                                marginTop(if (concern.description.isNotBlank()) 8.px else 12.px)
                                property("border-top", "1px solid rgba(0,0,0,0.06)")
                                paddingTop(10.px)
                            }
                        }) {
                            var selectedTab by remember { mutableStateOf(ReviewTab.ISLAMIC) }

                            // Tab toggle — pinned (position: sticky) below the
                            // concern header so you can switch perspective while
                            // reading, mirroring mobile's pinned tab bar. The
                            // sticky wrapper carries an opaque bg so the
                            // perspective text scrolls cleanly underneath it.
                            Div(attrs = {
                                style {
                                    property("position", "sticky")
                                    property("top", "32px")
                                    property("z-index", "3")
                                    backgroundColor(cardBg)
                                    paddingBottom(10.px)
                                }
                            }) {
                                Div(attrs = {
                                    style {
                                        display(DisplayStyle.Flex)
                                        backgroundColor(Gray100)
                                        borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                                        padding(3.px)
                                        gap(2.px)
                                    }
                                }) {
                                    MiniTabPill(
                                        label = "Islamic",
                                        isSelected = selectedTab == ReviewTab.ISLAMIC,
                                        activeColor = Primary,
                                        onClick = { selectedTab = ReviewTab.ISLAMIC }
                                    )
                                    MiniTabPill(
                                        label = "Developmental",
                                        isSelected = selectedTab == ReviewTab.DEVELOPMENTAL,
                                        activeColor = DevelopmentalText,
                                        onClick = { selectedTab = ReviewTab.DEVELOPMENTAL }
                                    )
                                }
                            }

                            if (selectedTab == ReviewTab.ISLAMIC) {
                                Div {
                                    concern.islamicPerspective?.let { perspective ->
                                        Div(attrs = {
                                            style {
                                                fontSize(13.px)
                                                fontWeight(700)
                                                property("background", GradientPrimary)
                                                property("-webkit-background-clip", "text")
                                                property("-webkit-text-fill-color", "transparent")
                                                property("background-clip", "text")
                                                marginBottom(6.px)
                                            }
                                        }) { Text("Islamic Perspective") }
                                        FormattedText(
                                            text = perspective,
                                            color = Gray600,
                                            lineHeight = 1.7.em
                                        )
                                    }
                                    concern.quranReference?.let { ref ->
                                        QuranReferenceBox(reference = ref)
                                    }
                                    concern.hadithReference?.let { ref ->
                                        HadithReferenceBox(reference = ref)
                                    }
                                }
                            } else {
                                concern.developmentalPerspective?.let { perspective ->
                                    Div {
                                        Div(attrs = {
                                            style {
                                                fontSize(13.px)
                                                fontWeight(700)
                                                color(DevelopmentalText)
                                                marginBottom(6.px)
                                            }
                                        }) { Text("Developmental Perspective") }
                                        FormattedText(
                                            text = perspective,
                                            color = Gray600,
                                            lineHeight = 1.7.em
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Every standard concern is listed, so a parent can see it was checked: the ones with
        // nothing found get a plain row under the findings. Nothing to expand, so not clickable.
        result.clearConcernCategories().forEach { category -> ClearConcernRow(category) }
    }

    // 3. Positive Aspects
    if (result.positiveAspects.isNotEmpty()) {
        PositiveAspectsSection(result.positiveAspects)
    }

    // 4. Alternatives / Similar Titles (live)
    AlternativesSection(result.score, alternatives, onAlternativeSelected)
    if ((result.islamicScore ?: 0) < 8) IslamicPicksSection(islamicAlternatives, onAlternativeSelected)
    if (!result.author.isNullOrBlank()) AuthorTitlesSection(result.author!!, authorAlternatives, onAlternativeSelected)
    CommentsSection(comments, likeCount, dislikeCount, myReaction, isSignedIn, isSubmittingComment, onReact, onPostComment, onEditComment, onDeleteComment, isFlagged, onFlagClick, commentActionError, title?.replace(trailingYearRe, "")?.trim(), reactionDetail)
    AnalysisDisclaimer()
}

/** A standard concern the analysis checked and found nothing for: the concern card's chrome with
 *  a "None found" badge, and no chevron since there's nothing to open. Mirrors composeApp. */
@Composable
private fun ClearConcernRow(category: String) {
    Div(attrs = {
        style {
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            backgroundColor(rgba(240, 255, 246, 1))
            padding(12.px)
            marginBottom(8.px)
            border(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.08))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            property("justify-content", "space-between")
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px); property("flex", "1") } }) {
            Span(attrs = { style { fontSize(12.px); fontWeight(700); color(ScoreGreen) } }) { Text("✓") }
            Span(attrs = { style { fontSize(16.px); fontWeight(700); color(Gray700) } }) { Text(concernDisplayName(category)) }
        }
        Span(attrs = {
            style {
                fontSize(10.px)
                fontWeight(700)
                color(ScoreGreen)
                backgroundColor(rgba(34, 197, 94, 0.12))
                borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                padding(3.px, 10.px)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
                property("flex-shrink", "0")
            }
        }) { Text("None found") }
    }
}

/** Small disclaimer pinned to the bottom of the analysis card. Moved here from the global footer
 *  so it sits right where the analysis is shown, not on every page. */
/** "What we found": the specific moments Phase 1 listed for this concern. Empty list → nothing. */
@Composable
private fun FoundMoments(instances: List<String>) {
    if (instances.isEmpty()) return
    Div(attrs = { style { marginTop(10.px) } }) {
        Div(attrs = {
            style {
                fontSize(11.px); fontWeight(800); color(Gray700)
                property("letter-spacing", "0.08em"); property("text-transform", "uppercase")
                marginBottom(6.px)
            }
        }) { Text("What we found") }
        instances.forEach { moment ->
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(8.px); marginBottom(4.px)
                    fontSize(14.px); color(Gray600); property("line-height", "1.45")
                }
            }) {
                Span(attrs = { style { color(Gray500) } }) { Text("•") }
                Span { Text(filterProfanity(moment)) }
            }
        }
    }
}

/** Stands in for a concern's full write-up while it is still being produced; the card fills in
 *  the moment it lands. Reuses the page's `.concerns-loading-bar` shimmer. */
@Composable
private fun WriteUpPlaceholder() {
    Div(attrs = { style { marginTop(12.px) } }) {
        Div(attrs = { style { fontSize(12.px); fontWeight(600); color(InfoText); marginBottom(10.px) } }) {
            Text("Generating Islamic and developmental guidance")
        }
        listOf(100, 90, 62).forEach { widthPct ->
            Div(attrs = {
                classes("concerns-loading-bar")
                style { width(widthPct.percent); height(10.px); borderRadius(5.px); marginBottom(8.px) }
            })
        }
    }
}

@Composable
private fun AnalysisDisclaimer() {
    Div(attrs = {
        style {
            property("text-align", "center")
            marginTop(20.px)
            fontSize(11.px)
            property("color", "$Gray400")
            property("line-height", "1.5")
        }
    }) { Text("Not a substitute for personal judgment or scholarly consultation") }
}

/**
 * Shared, public comment thread + thumbs up/down for the title on screen — every user who views
 * this title sees the same thread and tally. Deliberately minimal per the product brief: a
 * reaction row, a plain comment list (edit/delete only on the caller's own comments), and one
 * text box to add a comment. Signed-out users see everything read-only with a sign-in prompt.
 */
@Composable
private fun CommentsSection(
    comments: List<Comment>,
    likeCount: Int,
    dislikeCount: Int,
    myReaction: Int?,
    isSignedIn: Boolean,
    isSubmitting: Boolean,
    onReact: (Int) -> Unit,
    onPostComment: (String) -> Unit,
    onEditComment: (id: String, body: String) -> Unit,
    onDeleteComment: (id: String) -> Unit,
    isFlagged: Boolean,
    onFlagClick: (() -> Unit)?,
    commentActionError: String?,
    /** Bare title (year stripped) woven into the rating prompt; null falls back to short copy. */
    ratedTitle: String?,
    /** Optional extra content under the thumbs row — the admin dashboard uses it to list who
     *  reacted. Null in the public app. */
    reactionDetail: (@Composable () -> Unit)? = null,
) {
    Div {
        SectionCard {
            // Label above, controls below — same two-row shape as mobile so the prompt can run
            // to two lines without squeezing the buttons.
            Div(attrs = {
                style { fontSize(16.px); fontWeight(700); color(Gray900); property("line-height", "1.35") }
            }) {
                Text(
                    if (ratedTitle.isNullOrBlank()) "Did we help you decide?"
                    else "Did we help you decide if $ratedTitle is safe for your family?"
                )
            }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
                    marginTop(12.px)
                }
            }) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); property("flex-shrink", "0") } }) {
                    ReactionButton("/images/positivevote.png", "/images/positivevoteempty.png", likeCount, myReaction == 1, isSignedIn) { onReact(1) }
                    ReactionButton("/images/negativevote.png", "/images/negativevoteempty.png", dislikeCount, myReaction == -1, isSignedIn) { onReact(-1) }
                }
                Div(attrs = { style { property("flex", "1") } })
                // "This analysis looks wrong" flag — lives here rather than floating over the
                // cover art, so reporting sits with the other feedback controls.
                if (isFlagged) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(5.px)
                            padding(5.px, 10.px); borderRadius(999.px)
                            property("background", "#FEE2E2"); property("flex-shrink", "0")
                        }
                    }) {
                        Img(src = "/images/flag.png", attrs = { style { height(14.px); property("width", "auto") } })
                        Span(attrs = {
                            style {
                                fontSize(11.px); fontWeight(700); property("color", "#B91C1C")
                                property("white-space", "nowrap")
                            }
                        }) { Text("Flagged") }
                    }
                } else if (onFlagClick != null) {
                    Div(attrs = {
                        onClick { onFlagClick() }
                        attr("title", "Flag a problem with this analysis")
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                            width(32.px); height(32.px); borderRadius(50.percent)
                            border(1.px, LineStyle.Solid, Color.black)
                            property("box-sizing", "border-box")
                            cursor("pointer"); property("flex-shrink", "0")
                        }
                    }) {
                        Img(src = "/images/whiteflag.png", attrs = { style { height(20.px); property("width", "auto") } })
                    }
                }
            }
            // Admin-only detail slot (who cast each reaction). Null for every real user, so the
            // public thumbs row is untouched — which is also why mobile needs no matching change.
            reactionDetail?.invoke()
        }

        SectionCard {
        Div(attrs = {
            style { fontSize(16.px); fontWeight(700); color(Gray900); marginBottom(10.px) }
        }) { Text("Parents' notes") }

        comments.forEach { comment ->
            // Each note gets its own card so a long thread reads as discrete entries.
            Div(attrs = {
                style {
                    backgroundColor(rgb(250, 250, 250))
                    borderRadius(10.px)
                    border(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.07))
                    padding(10.px, 12.px)
                    marginBottom(10.px)
                }
            }) {
                CommentRow(
                    comment = comment,
                    onEdit = { body -> onEditComment(comment.id, body) },
                    onDelete = { onDeleteComment(comment.id) }
                )
            }
        }
        if (comments.isNotEmpty()) Div(attrs = { style { height(6.px) } })

        if (isSignedIn) {
            var draft by remember { mutableStateOf("") }
            val canPost = !isSubmitting && draft.isNotBlank()
            // Post lives INSIDE the rounded container rather than beside it, so the composer
            // reads as one pill-shaped input (matches mobile's trailingIcon treatment).
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px)
                    borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
                    border(1.5.px, LineStyle.Solid, Gray300)
                    padding(2.px, 6.px, 2.px, 14.px)
                    backgroundColor(Color.white)
                    property("box-sizing", "border-box")
                }
            }) {
                TextArea(value = draft, attrs = {
                    onInput { draft = it.value }
                    // Starts one line tall (matching mobile); the user can drag it taller.
                    attr("rows", "1")
                    attr("placeholder", "Add a note for other parents…")
                    // Sentence case on touch keyboards, matching mobile.
                    attr("autocapitalize", "sentences")
                    style {
                        property("flex", "1"); property("box-sizing", "border-box")
                        padding(10.px, 0.px)
                        property("border", "none")
                        property("background", "transparent")
                        fontSize(14.px); property("font-family", "inherit"); property("resize", "none")
                        property("outline", "none")
                    }
                })
                Div(attrs = {
                    if (canPost) onClick { onPostComment(draft); draft = "" }
                    style {
                        padding(8.px, 16.px); borderRadius(999.px)
                        backgroundColor(if (canPost) Primary else Gray300)
                        color(Color.white); fontSize(14.px); fontWeight(700)
                        cursor(if (canPost) "pointer" else "not-allowed")
                        property("white-space", "nowrap"); property("flex-shrink", "0")
                    }
                }) { Text("Post") }
            }
        } else {
            Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Sign in to leave a note") }
        }

        // Was previously set but never rendered — a failed post looked like nothing happened.
        if (commentActionError != null) {
            Div(attrs = {
                style { fontSize(12.px); property("color", "#B91C1C"); marginTop(8.px) }
            }) { Text(commentActionError) }
        }
        }
    }
}

/** Bare thumb icon with its tally tucked into the bottom-right corner — no pill chrome. */
@Composable
private fun ReactionButton(filledSrc: String, emptySrc: String, count: Int, isActive: Boolean, enabled: Boolean, onReactClick: () -> Unit) {
    Div(attrs = {
        if (enabled) onClick { onReactClick() }
        style {
            property("position", "relative")
            // A few px larger than the 32px icon: the extra space is what pushes the
            // bottom-right badge down-and-right across the icon's arc.
            width(39.px); height(39.px)
            property("flex-shrink", "0")
            cursor(if (enabled) "pointer" else "default")
        }
    }) {
        // Filled/colored icon indicates the user's own reaction — never tied to the count,
        // which may be nonzero even when this caller hasn't reacted.
        Img(src = if (isActive) filledSrc else emptySrc, attrs = {
            style {
                property("position", "absolute")
                property("top", "0"); property("left", "0")
                height(32.px); property("width", "auto")
            }
        })
        // Count badge — sits ON TOP of the icon's lower-right arc (explicit z-index; the
        // container is only slightly larger than the icon so the badge lands on the artwork
        // rather than in the empty corner beside it).
        Span(attrs = {
            style {
                property("position", "absolute")
                property("bottom", "0"); property("right", "0")
                property("z-index", "1")
                width(18.px); height(18.px)
                borderRadius(50.percent)
                backgroundColor(Color.white)
                border(1.px, LineStyle.Solid, Color.black)
                property("box-sizing", "border-box")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                fontSize(10.px); fontWeight(700)
                color(if (isActive) Primary else Gray700)
                property("line-height", "1")
            }
        }) { Text(count.toString()) }
    }
}

@Composable
private fun CommentRow(comment: Comment, onEdit: (String) -> Unit, onDelete: () -> Unit) {
    var isEditing by remember(comment.id) { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var draft by remember(comment.id) { mutableStateOf(comment.body) }
    val nowMs = remember { com.islamicmedia.shared.util.currentTimeMillis() }

    Div(attrs = { style { marginBottom(4.px) } }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
            Span(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray900) } }) {
                Text(comment.firstName.ifBlank { "Anonymous" })
            }
            if (comment.isMine) {
                Span(attrs = { style { fontSize(11.px); color(Gray500) } }) { Text("(you)") }
            }
            Span(attrs = { style { fontSize(11.px); color(Gray500) } }) {
                Text(com.islamicmedia.shared.util.formatRelativeTime(comment.createdAt, nowMs))
            }
            if (comment.editedAt != null) {
                Span(attrs = { style { fontSize(11.px); color(Gray500) } }) { Text("(edited)") }
            }
            if (comment.isMine && !isEditing) {
                Div(attrs = { style { property("flex", "1") } })
                Img(src = "/images/edit.png", attrs = {
                    onClick { isEditing = true; draft = comment.body }
                    attr("title", "Edit note")
                    attr("alt", "Edit note")
                    style { height(18.px); property("width", "auto"); cursor("pointer") }
                })
                Img(src = "/images/trash.png", attrs = {
                    onClick { showDeleteConfirm = true }
                    attr("title", "Delete note")
                    attr("alt", "Delete note")
                    style {
                        height(18.px); property("width", "auto"); cursor("pointer")
                        property("margin-left", "14px")
                    }
                })
            }
        }
        if (isEditing) {
            TextArea(value = draft, attrs = {
                onInput { draft = it.value }
                attr("rows", "2")
                attr("autocapitalize", "sentences")
                style {
                    width(100.percent); property("box-sizing", "border-box")
                    marginTop(4.px); padding(8.px); borderRadius(8.px)
                    property("border", "1.5px solid $Gray300")
                    fontSize(14.px); property("font-family", "inherit"); property("resize", "vertical")
                    property("outline", "none")
                }
            })
            Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); marginTop(4.px) } }) {
                Span(attrs = {
                    if (draft.isNotBlank()) onClick { isEditing = false; onEdit(draft) }
                    style {
                        fontSize(13.px); fontWeight(700)
                        color(if (draft.isNotBlank()) Primary else Gray400)
                        cursor(if (draft.isNotBlank()) "pointer" else "default")
                    }
                }) { Text("Save") }
                Span(attrs = {
                    onClick { isEditing = false; draft = comment.body }
                    style { fontSize(13.px); color(Gray500); cursor("pointer") }
                }) { Text("Cancel") }
            }
        } else {
            Div(attrs = {
                style { fontSize(14.px); color(Gray700); property("line-height", "1.5"); marginTop(4.px) }
            }) { Text(comment.body) }
        }
    }

    if (showDeleteConfirm) {
        Div(attrs = {
            onClick { showDeleteConfirm = false }
            style {
                property("position", "fixed"); property("inset", "0")
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white); borderRadius(16.px); padding(28.px)
                    property("max-width", "400px"); width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
                }
            }) {
                Div(attrs = {
                    style { fontSize(18.px); fontWeight(600); color(Gray900); marginBottom(12.px) }
                }) { Text("Delete note") }
                Div(attrs = {
                    style { fontSize(14.px); color(Gray600); property("line-height", "1.5"); marginBottom(24.px) }
                }) { Text("Are you sure you want to delete this note? This action cannot be undone.") }
                Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(12.px) } }) {
                    Div(attrs = {
                        onClick { showDeleteConfirm = false }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px)
                            backgroundColor(Gray100); color(Gray700); fontSize(14.px); fontWeight(500)
                            cursor("pointer")
                        }
                    }) { Text("Cancel") }
                    Div(attrs = {
                        onClick { showDeleteConfirm = false; onDelete() }
                        style {
                            padding(8.px, 20.px); borderRadius(8.px)
                            backgroundColor(Color("#EF4444")); color(Color.white); fontSize(14.px); fontWeight(500)
                            cursor("pointer")
                        }
                    }) { Text("Delete") }
                }
            }
        }
    }
}

@Composable
private fun IslamicPicksSection(
    picks: List<AlternativeItem>,
    onSelect: (AlternativeItem, Boolean) -> Unit
) = TitledPicksSection("More Islamic Picks", "Highly Islamic-rated, same media type", picks, onSelect)

/** "More by {author}" row — other green titles by the source's author. Same layout/behavior as
 *  [IslamicPicksSection], just a different heading and source list. */
@Composable
private fun AuthorTitlesSection(
    author: String,
    picks: List<AlternativeItem>,
    onSelect: (AlternativeItem, Boolean) -> Unit
) = TitledPicksSection("More by $author", "Other titles by the same author", picks, onSelect)

@Composable
private fun TitledPicksSection(
    title: String,
    subtitle: String,
    picks: List<AlternativeItem>,
    onSelect: (AlternativeItem, Boolean) -> Unit
) {
    if (picks.isEmpty()) return
    var expanded by remember { mutableStateOf(false) }
    var showModal by remember { mutableStateOf(false) }
    var previewAlt by remember { mutableStateOf<AlternativeItem?>(null) }

    SectionCard {
        Div(attrs = {
            onClick { expanded = !expanded }
            // Full-width block so the whole header row toggles, not just the title + chevron.
            style {
                width(100.percent)
                cursor("pointer")
            }
        }) {
            Div {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(2.px)
                        fontSize(16.px); fontWeight(700); color(Gray900); marginBottom(2.px)
                    }
                }) {
                    Span { Text(title) }
                    ChevronToggleIcon(expanded)
                }
                Div(attrs = { style { fontSize(12.px); color(Gray500) } }) {
                    Text(subtitle)
                }
            }
        }
        if (expanded) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Row); gap(10.px)
                    property("overflow-x", "auto")
                    property("padding-top", "10px")
                    property("padding-bottom", "6px")
                    property("-webkit-overflow-scrolling", "touch")
                }
            }) {
                picks.take(5).forEach { pick -> AlternativeCard(pick) { alt -> previewAlt = alt } }
                if (picks.size > 5) SeeMoreCard(picks.size) { showModal = true }
            }
        }
    }

    if (previewAlt != null) TitlePreviewModal(
        alt = previewAlt!!,
        onDismiss = { previewAlt = null },
        onSelect = { alt, isQuick -> previewAlt = null; onSelect(alt, isQuick) }
    )

    if (showModal) RecommendationsModal(
        title = title,
        items = picks,
        onDismiss = { showModal = false },
        onSelect = { alt -> previewAlt = alt }
    )
}

@Composable
fun AnalysisResultContent(result: AnalysisResult) {
    // Score Section
    ScoreSection(score = result.score, recommendation = result.recommendation, summary = result.summary, ageGuidance = result.ageGuidance, concernCount = result.concerns.size, quickCheck = result.quickCheck)

    // Concern list — most critical first
    val sortedConcerns = result.concerns.sortedBy {
        when (it.severity.lowercase()) {
            "high" -> 0
            "medium" -> 1
            else -> 2
        }
    }
    sortedConcerns.forEachIndexed { index, concern ->
        ConcernListItem(
            concern = concern,
            index = index
        )
    }

    // Positive Aspects
    if (result.positiveAspects.isNotEmpty()) {
        PositiveAspectsSection(result.positiveAspects)
    }
}

/**
 * Live "Alternatives" (red/yellow source) or "Similar Titles" (green source) section. Fetched from
 * /api/alternatives — never part of AnalysisResult. Only ever shows green (score>=7) same-type picks.
 */
@Composable
private fun AlternativesSection(
    sourceScore: Int,
    alternatives: List<AlternativeItem>,
    onSelect: (AlternativeItem, Boolean) -> Unit
) {
    // Nothing in the catalog for this title → no section at all. No placeholder, no "we're looking"
    // copy: the fetch is a plain catalog read and an empty answer is final.
    if (alternatives.isEmpty()) return
    val isGreen = sourceScore >= 8
    var expanded by remember { mutableStateOf(false) }
    var showModal by remember { mutableStateOf(false) }
    var previewAlt by remember { mutableStateOf<AlternativeItem?>(null) }

    SectionCard {
        Div(attrs = {
            onClick { expanded = !expanded }
            // Full-width block so the whole header row toggles, not just the title + chevron.
            style {
                width(100.percent)
                cursor("pointer")
            }
        }) {
            Div {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(2.px)
                        fontSize(16.px); fontWeight(700); color(Gray900); marginBottom(2.px)
                    }
                }) {
                    Span { Text(if (isGreen) "Similar Titles" else "Alternatives") }
                    ChevronToggleIcon(expanded)
                }
                Div(attrs = { style { fontSize(12.px); color(Gray500) } }) {
                    Text(if (isGreen) "More like this that Muslim families can enjoy"
                         else "Safer picks")
                }
            }
        }

        if (expanded) {
            Div(attrs = { style { paddingTop(10.px) } }) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); flexDirection(FlexDirection.Row); gap(10.px)
                        property("overflow-x", "auto")
                        property("padding-bottom", "6px")
                        property("-webkit-overflow-scrolling", "touch")
                    }
                }) {
                    alternatives.take(5).forEach { alt -> AlternativeCard(alt) { a -> previewAlt = a } }
                    if (alternatives.size > 5) SeeMoreCard(alternatives.size) { showModal = true }
                }
            }
        }
    }

    if (previewAlt != null) TitlePreviewModal(
        alt = previewAlt!!,
        onDismiss = { previewAlt = null },
        onSelect = { alt, isQuick -> previewAlt = null; onSelect(alt, isQuick) }
    )

    if (showModal) RecommendationsModal(
        title = if (isGreen) "Similar Titles" else "Alternatives",
        items = alternatives,
        onDismiss = { showModal = false },
        onSelect = { alt -> previewAlt = alt }
    )
}

@Composable
private fun SeeMoreCard(totalCount: Int, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            width(156.px); property("flex-shrink", "0")
            height(330.px)  // 160 cover + 170 text — matches AlternativeCard
            backgroundColor(rgb(255, 255, 255))
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            border(2.px, LineStyle.Solid, rgba(0, 0, 0, 0.1))
            cursor("pointer")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            gap(12.px)
            property("transition", TransitionFast)
        }
    }) {
        // 2×2 grid-dots icon
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, 10px)")
                property("grid-template-rows", "repeat(2, 10px)")
                gap(5.px)
            }
        }) {
            repeat(4) {
                Div(attrs = {
                    style {
                        width(10.px); height(10.px)
                        backgroundColor(Gray300)
                        borderRadius(3.px)
                    }
                })
            }
        }
        Div(attrs = {
            style { fontSize(13.px); fontWeight(600); color(Gray700) }
        }) { Text("See all $totalCount") }
    }
}

@Composable
private fun TitlePreviewModal(
    alt: AlternativeItem,
    onDismiss: () -> Unit,
    onSelect: (AlternativeItem, Boolean) -> Unit
) {
    val dotColor = when { alt.score >= 7 -> ScoreGreen; alt.score >= 4 -> ScoreAmber; else -> ScoreRed }
    // Full-screen backdrop
    Div(attrs = {
        onClick { onDismiss() }
        style {
            position(Position.Fixed)
            top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            property("z-index", "1000")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(16.px)
        }
    }) {
        // Modal panel
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                backgroundColor(rgb(255, 255, 255))
                borderRadius(20.px)
                property("width", "min(440px, 94vw)")
                property("max-height", "90vh")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                property("overflow", "hidden")
                position(Position.Relative)
            }
        }) {
            // Cover image area (260px tall)
            Div(attrs = {
                style {
                    position(Position.Relative)
                    width(100.percent)
                    height(260.px)
                    property("flex-shrink", "0")
                }
            }) {
                val cover = alt.coverUrl?.takeIf { it.isNotBlank() }
                if (cover != null) {
                    Img(src = cover, alt = alt.title, attrs = {
                        style {
                            width(100.percent); height(260.px)
                            property("object-fit", "cover")
                            display(DisplayStyle.Block)
                        }
                    })
                } else {
                    Div(attrs = { style { width(100.percent); height(260.px); backgroundColor(Gray100) } })
                }
                // Score dot top-right
                Div(attrs = {
                    style {
                        position(Position.Absolute); top(12.px); right(12.px)
                        width(12.px); height(12.px); borderRadius(50.percent)
                        backgroundColor(dotColor)
                    }
                })
                // Close button top-right (just inside the dot)
                Div(attrs = {
                    onClick { onDismiss() }
                    style {
                        position(Position.Absolute); top(12.px); right(32.px)
                        width(28.px); height(28.px); borderRadius(50.percent)
                        backgroundColor(Gray100)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        cursor("pointer"); fontSize(12.px); color(Gray500)
                    }
                }) { Text("✕") }
            }
            // Text section
            Div(attrs = {
                style {
                    padding(16.px)
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px)
                    property("overflow-y", "auto")
                    property("flex", "1")
                }
            }) {
                // Title
                Div(attrs = {
                    style {
                        fontSize(17.px); fontWeight(700); color(Gray900)
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                    }
                }) { Text(alt.title) }
                // Year
                alt.year?.takeIf { it.isNotBlank() }?.let { year ->
                    Div(attrs = { style { fontSize(12.px); color(Gray500) } }) { Text(year) }
                }
                // Summary
                alt.summary?.takeIf { it.isNotBlank() }?.let { desc ->
                    Div(attrs = {
                        style {
                            fontSize(12.px); color(Gray500)
                            property("display", "-webkit-box")
                            property("-webkit-line-clamp", "8")
                            property("-webkit-box-orient", "vertical")
                            property("overflow", "hidden")
                            property("line-height", "1.5")
                        }
                    }) { Text(desc) }
                }
            }
            // Button row
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Row)
                    gap(8.px)
                    padding(16.px)
                    property("flex-shrink", "0")
                }
            }) {
                // Quick Analyze (outlined) — only while the quick/full choice exists; otherwise
                // every search is FULL.
                if (AppConfig.SEARCH_MODE_CHOICE_ENABLED) {
                    Button(attrs = {
                        attr("type", "button")
                        onClick { onSelect(alt, true) }
                        style {
                            property("flex", "1")
                            height(36.px)
                            borderRadius(10.px)
                            property("background", "white")
                            property("border", "1.5px solid $Primary")
                            color(Primary)
                            fontSize(12.px)
                            fontWeight(600)
                            cursor("pointer")
                            property("font-family", "inherit")
                        }
                    }) { Text("Quick Analyze") }
                }
                // Full Analyze (filled)
                Button(attrs = {
                    attr("type", "button")
                    onClick { onSelect(alt, false) }
                    style {
                        property("flex", "1")
                        height(36.px)
                        borderRadius(10.px)
                        backgroundColor(Primary)
                        property("border", "none")
                        color(Color.white)
                        fontSize(12.px)
                        fontWeight(600)
                        cursor("pointer")
                        property("font-family", "inherit")
                    }
                }) { Text("Full Analyze") }
            }
        }
    }
}

@Composable
private fun RecommendationsModal(
    title: String,
    items: List<AlternativeItem>,
    onDismiss: () -> Unit,
    onSelect: (AlternativeItem) -> Unit
) {
    // Full-screen backdrop
    Div(attrs = {
        onClick { onDismiss() }
        style {
            position(Position.Fixed)
            top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            property("z-index", "1000")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(16.px)
        }
    }) {
        // Modal panel — stop click propagation so clicking inside doesn't close it
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                backgroundColor(rgb(255, 255, 255))
                borderRadius(20.px)
                property("width", "min(860px, 96vw)")
                property("max-height", "90vh")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                property("overflow", "hidden")
            }
        }) {
            // Header
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Row)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)
                    padding(20.px, 24.px, 16.px, 24.px)
                    property("flex-shrink", "0")
                }
            }) {
                Div(attrs = { style { fontSize(17.px); fontWeight(700); color(Gray900) } }) { Text(title) }
                Div(attrs = {
                    onClick { onDismiss() }
                    style {
                        width(28.px); height(28.px); borderRadius(50.percent)
                        backgroundColor(Gray100)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        cursor("pointer"); fontSize(13.px); color(Gray500)
                    }
                }) { Text("✕") }
            }
            // Scrollable flex-wrap row — cards keep their natural 156px width
            Div(attrs = {
                style {
                    property("overflow-y", "auto")
                    padding(0.px, 16.px, 20.px, 16.px)
                    display(DisplayStyle.Flex)
                    property("flex-wrap", "wrap")
                    gap(10.px)
                }
            }) {
                items.forEach { item ->
                    AlternativeCard(item, onSelect)
                }
            }
        }
    }
}

@Composable
private fun AlternativeCard(alt: AlternativeItem, onSelect: (AlternativeItem) -> Unit) {
    Div(attrs = {
        onClick { onSelect(alt) }  // opens preview at section level
        style {
            width(156.px); property("flex-shrink", "0")
            backgroundColor(rgb(255, 255, 255))
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            // No verdict colouring at all here — neither the border nor the score dot. These are
            // suggestions the user hasn't asked about yet; scoring each one turned the row into a
            // wall of traffic lights. (Mirrors AlternativeCard in composeApp.)
            cursor("pointer")
            property("transition", TransitionFast)
            property("overflow", "hidden")
        }
    }) {
        // Poster cover (160px) with verdict dot overlaid top-right
        Div(attrs = {
            style {
                position(Position.Relative)
                width(100.percent); height(160.px)
                property("flex-shrink", "0")
            }
        }) {
            val cover = alt.coverUrl?.takeIf { it.isNotBlank() }
            if (cover != null) {
                Img(src = cover, alt = alt.title, attrs = {
                    style {
                        width(100.percent); height(160.px)
                        property("object-fit", "cover"); display(DisplayStyle.Block)
                    }
                })
            } else {
                Div(attrs = { style { width(100.percent); height(160.px); backgroundColor(rgba(0, 0, 0, 0.06)) } })
            }
        }
        // Fixed-height text block — enforces uniform card height
        Div(attrs = {
            style {
                height(170.px); property("overflow", "hidden")
                padding(8.px, 10.px)
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px)
            }
        }) {
            Div(attrs = {
                style {
                    fontSize(13.px); fontWeight(600); color(Gray900)
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                }
            }) { Text(alt.title) }
            alt.summary?.takeIf { it.isNotBlank() }?.let { desc ->
                Div(attrs = {
                    style {
                        fontSize(11.px); color(Gray500)
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "7")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("line-height", "1.4")
                    }
                }) { Text(desc) }
            }
        }
    }
}

@Composable
private fun TldrSkeleton(score: Int) {
    val bgGradient = when {
        score >= 7 -> "linear-gradient(135deg, rgba(220, 252, 231, 0.82) 0%, rgba(187, 247, 208, 0.82) 100%)"
        score >= 4 -> "linear-gradient(135deg, rgba(254, 243, 199, 0.82) 0%, rgba(253, 230, 138, 0.82) 100%)"
        else       -> "linear-gradient(135deg, rgba(254, 226, 226, 0.82) 0%, rgba(252, 165, 165, 0.82) 100%)"
    }
    InjectStyles(
        "tldr-shimmer-styles",
        """
        @keyframes tldr-shimmer {
            0%   { opacity: 0.35; }
            50%  { opacity: 0.7; }
            100% { opacity: 0.35; }
        }
        """.trimIndent()
    )
    Div(attrs = {
        style {
            property("background", bgGradient)
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            padding(16.px)
            marginBottom(16.px)
            border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.45))
            property("backdrop-filter", "blur(6px)")
            property("-webkit-backdrop-filter", "blur(6px)")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                marginBottom(14.px)
            }
        }) {
            Div(attrs = {
                style {
                    fontSize(11.px); fontWeight(800); color(Color.black)
                    property("letter-spacing", "0.08em"); property("text-transform", "uppercase")
                }
            }) { Text("HIGHLIGHTS") }
            Div(attrs = {
                style {
                    fontSize(10.px); fontWeight(600); color(Gray700)
                    property("font-style", "italic"); property("opacity", "0.7")
                }
            }) { Text("generating…") }
        }
        listOf(90, 55, 75).forEach { widthPct ->
            Div(attrs = {
                style {
                    width(widthPct.percent); height(24.px)
                    borderRadius(8.px)
                    backgroundColor(rgba(0, 0, 0, 0.10))
                    marginBottom(10.px)
                    property("animation", "tldr-shimmer 1.4s ease-in-out infinite")
                }
            })
        }
    }
}

@Composable
private fun TldrSection(tldr: List<TldrPoint>, score: Int) {
    // Preserve insertion order while grouping by category
    val groups: List<Pair<String?, List<TldrPoint>>> = run {
        val order = mutableListOf<String?>()
        val map = linkedMapOf<String?, MutableList<TldrPoint>>()
        tldr.forEach { point ->
            val key = point.category?.takeIf { it.isNotBlank() }
            if (!map.containsKey(key)) { order.add(key); map[key] = mutableListOf() }
            map[key]!!.add(point)
        }
        order.map { key -> key to (map[key] ?: emptyList()) }
    }
    val bgGradient = when {
        score >= 7 -> "linear-gradient(135deg, rgba(220, 252, 231, 0.82) 0%, rgba(187, 247, 208, 0.82) 100%)"
        score >= 4 -> "linear-gradient(135deg, rgba(254, 243, 199, 0.82) 0%, rgba(253, 230, 138, 0.82) 100%)"
        else       -> "linear-gradient(135deg, rgba(254, 226, 226, 0.82) 0%, rgba(252, 165, 165, 0.82) 100%)"
    }

    Div(attrs = {
        style {
            property("background", bgGradient)
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            padding(16.px)
            marginBottom(16.px)
            border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.45))
            property("backdrop-filter", "blur(6px)")
            property("-webkit-backdrop-filter", "blur(6px)")
        }
    }) {
        Div(attrs = {
            style {
                fontSize(11.px); fontWeight(800); color(Color.black)
                property("letter-spacing", "0.08em"); property("text-transform", "uppercase")
                marginBottom(12.px)
            }
        }) { Text("HIGHLIGHTS") }

        groups.forEachIndexed { i, (category, points) ->
            if (i > 0) {
                Hr(attrs = {
                    style {
                        property("border", "none")
                        property("border-top", "1px solid rgba(0,0,0,0.06)")
                        margin(10.px, 0.px)
                    }
                })
            }
            if (category != null) {
                Div(attrs = {
                    style {
                        fontSize(12.px); fontWeight(700); color(Gray700)
                        marginBottom(6.px)
                    }
                }) { Text(concernDisplayName(category)) }
            }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(6.px)
                }
            }) {
                points.forEach { point ->
                    val (chipColor, chipBg) = when (point.severity.lowercase()) {
                        "high"     -> ScoreRed   to rgba(239, 68, 68, 0.12)
                        "positive" -> ScoreGreen to rgba(34, 197, 94, 0.12)
                        else       -> ScoreAmber to rgba(245, 158, 11, 0.12)
                    }
                    Span(attrs = {
                        style {
                            display(DisplayStyle.InlineBlock); padding(5.px, 12.px)
                            borderRadius(8.px); backgroundColor(chipBg); color(chipColor)
                            fontSize(12.px); fontWeight(600); property("line-height", "1.5")
                        }
                    }) { Text(filterProfanity(point.text)) }
                }
            }
        }
    }
}

@Composable
private fun ScoreSection(score: Int, recommendation: String, summary: String? = null, ageGuidance: String? = null, topConcerns: List<Concern> = emptyList(), concernCount: Int = 0, quickCheck: Boolean = false, onConcernClick: (Concern) -> Unit = {}) {
    val dotColor = when {
        score >= 7 -> ScoreGreen
        score >= 4 -> ScoreAmber
        else -> ScoreRed
    }
    val statusLabel = verdictLabel(score, concernCount) ?: ""
    // Slightly translucent so the cover backdrop on the outer ResultsWidget
    // card bleeds through softly behind the verdict block.
    val bgGradient = when {
        score >= 7 -> "linear-gradient(135deg, rgba(220, 252, 231, 0.82) 0%, rgba(187, 247, 208, 0.82) 100%)"
        score >= 4 -> "linear-gradient(135deg, rgba(254, 243, 199, 0.82) 0%, rgba(253, 230, 138, 0.82) 100%)"
        else -> "linear-gradient(135deg, rgba(254, 226, 226, 0.82) 0%, rgba(252, 165, 165, 0.82) 100%)"
    }

    Div(attrs = {
        style {
            property("background", bgGradient)
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            padding(24.px)
            marginTop(24.px)
            marginBottom(24.px)
            border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.45))
            property("backdrop-filter", "blur(6px)")
            property("-webkit-backdrop-filter", "blur(6px)")
        }
    }) {
        // "Final Verdict" label — with the Quick-check pill to its right on quick checks.
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "space-between")
                gap(8.px)
                marginBottom(8.px)
            }
        }) {
            Div(attrs = {
                style {
                    fontSize(11.px)
                    fontWeight(700)
                    color(Color.black)
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                }
            }) { Text("Final Verdict") }
            if (quickCheck) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                        padding(2.px, 8.px)
                        borderRadius(999.px)
                        property("background", "#FACC15")
                        property("box-shadow", "0 1px 3px rgba(0,0,0,0.2)")
                    }
                }) {
                    Img(src = "/images/quick.png", attrs = {
                        style {
                            height(13.px)
                            property("width", "auto")
                        }
                    })
                    Span(attrs = {
                        style {
                            fontSize(10.px)
                            fontWeight(700)
                            property("font-style", "italic")
                            property("color", "#4B5563")
                            property("white-space", "nowrap")
                        }
                    }) { Text("Quick check") }
                }
            }
        }

        // Status bubble
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                property("display", "inline-flex")
                padding(8.px, 14.px)
                borderRadius(8.px)
                backgroundColor(rgba(
                    when { score >= 7 -> 34; score >= 4 -> 245; else -> 239 },
                    when { score >= 7 -> 197; score >= 4 -> 158; else -> 68 },
                    when { score >= 7 -> 94; score >= 4 -> 11; else -> 68 },
                    0.12
                ))
                marginBottom(if (topConcerns.isNotEmpty()) 12.px else 16.px)
            }
        }) {
            // Safety-tier shield instead of a bare dot — the glyph carries the verdict.
            SafetyShieldIcon(
                score = score,
                hex = when { score >= 7 -> "22C55E"; score >= 4 -> "F59E0B"; else -> "EF4444" }
            )
            Span(attrs = {
                style {
                    fontSize(13.px)
                    fontWeight(800)
                    color(dotColor)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.04em")
                }
            }) { Text(statusLabel) }
        }

        // Top concerns
        if (topConcerns.isNotEmpty()) {
            Div(attrs = {
                style {
                    fontSize(11.px)
                    fontWeight(700)
                    color(Color.black)
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                    marginBottom(6.px)
                    marginTop(14.px)
                }
            }) { Text("Top Concerns") }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    property("flex-wrap", "wrap")
                    gap(6.px)
                    marginBottom(16.px)
                }
            }) {
                topConcerns.forEach { concern ->
                    val (bubbleColor, bubbleBg) = when (concern.severity.lowercase()) {
                        "high" -> ScoreRed to rgba(239, 68, 68, 0.12)
                        "medium" -> ScoreAmber to rgba(245, 158, 11, 0.12)
                        else -> ScoreGreen to rgba(34, 197, 94, 0.12)
                    }
                    Span(attrs = {
                        onClick { onConcernClick(concern) }
                        style {
                            display(DisplayStyle.InlineBlock)
                            padding(5.px, 12.px)
                            borderRadius(8.px)
                            backgroundColor(bubbleBg)
                            color(bubbleColor)
                            fontSize(12.px)
                            fontWeight(600)
                            cursor("pointer")
                            property("transition", "opacity 0.15s ease")
                        }
                        onMouseOver { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.opacity = "0.7" }
                        onMouseOut { (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.opacity = "1" }
                    }) { Text(concernDisplayName(concern.category)) }
                }
            }
        }

        // About — content description (summary_text), always shown when present
        if (!summary.isNullOrBlank()) {
            Div(attrs = { style { marginTop(16.px) } }) {
                Div(attrs = {
                    style {
                        fontSize(11.px); fontWeight(700); color(Color.black)
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        marginBottom(4.px)
                    }
                }) { Text("Description") }
                P(attrs = {
                    style { margin(0.px); fontSize(14.px); lineHeight(1.7.em); color(Gray500); fontWeight(400) }
                }) { Text(summary) }
            }
        }

        // Summary — full analysis recommendation only (not duplicated for quick checks)
        if (recommendation.isNotBlank()) {
            Div(attrs = { style { marginTop(16.px) } }) {
                Div(attrs = {
                    style {
                        fontSize(11.px); fontWeight(700); color(Color.black)
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        marginBottom(4.px)
                    }
                }) { Text("Summary") }
                P(attrs = {
                    style { margin(0.px); fontSize(14.px); lineHeight(1.7.em); color(Gray500); fontWeight(400) }
                }) { Text(recommendation) }
            }
        }

        // Age Guidance
        ageGuidance?.let { age ->
            val shortAge = age.split(";").first().split(".").first().trim() + "."
            Div(attrs = {
                style {
                    marginTop(14.px)
                }
            }) {
                Div(attrs = {
                    style {
                        fontSize(11.px)
                        fontWeight(700)
                        color(Color.black)
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        marginBottom(4.px)
                    }
                }) { Text("Age Guidance") }
                P(attrs = {
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        color(Gray500)
                        fontWeight(400)
                    }
                }) { Text(shortAge) }
            }
        }
    }
}

@Composable
private fun ConcernListItem(
    concern: Concern,
    index: Int
) {
    val hasIslamic = concern.islamicPerspective != null || concern.quranReference != null || concern.hadithReference != null
    val hasDevelopmental = concern.developmentalPerspective != null
    val hasPerspectives = hasIslamic || hasDevelopmental

    var selectedTab by remember { mutableStateOf(ReviewTab.ISLAMIC) }

    val cardBg: CSSColorValue = when (concern.severity.lowercase()) {
        "high" -> rgba(250, 235, 235, 1)
        "medium" -> rgba(248, 242, 228, 1)
        else -> rgba(235, 248, 240, 1)
    }
    val (badgeLabel, badgeColor, badgeBg) = when (concern.severity.lowercase()) {
        "high" -> Triple("Critical", ScoreRed, rgba(239, 68, 68, 0.12))
        "medium" -> Triple("Moderate", ScoreAmber, rgba(245, 158, 11, 0.12))
        else -> Triple("Mild", ScoreGreen, rgba(34, 197, 94, 0.12))
    }

    Div(attrs = {
        style {
            backgroundColor(cardBg)
            borderRadius(RadiusLarge.unsafeCast<CSSNumeric>())
            padding(24.px)
            marginBottom(16.px)
            property("box-shadow", NeuBoxShadow)
        }
    }) {
        // Header: category + severity badge
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "space-between")
                marginBottom(10.px)
            }
        }) {
            // Left: category
            Span(attrs = {
                style {
                    fontSize(16.px)
                    fontWeight(700)
                    color(Gray900)
                }
            }) { Text(concernDisplayName(concern.category)) }
            // Right: severity badge
            Span(attrs = {
                style {
                    fontSize(11.px)
                    fontWeight(700)
                    color(badgeColor)
                    backgroundColor(badgeBg)
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    padding(4.px, 12.px)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.05em")
                    property("flex-shrink", "0")
                }
            }) { Text(badgeLabel) }
        }

        // Description
        Div(attrs = {
            style {
                marginBottom(if (hasPerspectives) 12.px else 0.px)
            }
        }) {
            if (concern.description.isNotBlank()) {
                FormattedText(
                    text = concern.description,
                    color = Gray600,
                    headingColor = Gray700,
                    lineHeight = 1.7.em
                )
            }
        }

        // Mini perspective toggle (only if there's content for at least one tab)
        if (hasPerspectives) {
            // Mini pill toggle
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    backgroundColor(Gray100)
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    padding(3.px)
                    marginBottom(10.px)
                    gap(2.px)
                }
            }) {
                MiniTabPill(
                    label = "Islamic",
                    isSelected = selectedTab == ReviewTab.ISLAMIC,
                    activeColor = Primary,
                    onClick = { selectedTab = ReviewTab.ISLAMIC }
                )
                MiniTabPill(
                    label = "Developmental",
                    isSelected = selectedTab == ReviewTab.DEVELOPMENTAL,
                    activeColor = DevelopmentalText,
                    onClick = { selectedTab = ReviewTab.DEVELOPMENTAL }
                )
            }

            // Perspective content with smooth height animation
            val perspectiveRef = remember { mutableStateOf<HTMLElement?>(null) }
            val prevHeight = remember { object { var value = 0 } }

            LaunchedEffect(selectedTab) {
                val el = perspectiveRef.value ?: return@LaunchedEffect
                val startH = prevHeight.value
                if (startH <= 0) {
                    prevHeight.value = el.offsetHeight
                    return@LaunchedEffect
                }
                el.style.setProperty("height", "${startH}px")
                el.style.setProperty("overflow", "hidden")
                delay(20)
                el.style.setProperty("height", "auto")
                val endH = el.offsetHeight
                el.style.setProperty("height", "${startH}px")
                el.offsetHeight // force reflow
                el.style.setProperty("transition", "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)")
                el.style.setProperty("height", "${endH}px")
                delay(320)
                prevHeight.value = endH
                el.style.removeProperty("transition")
                el.style.removeProperty("height")
                el.style.removeProperty("overflow")
            }

            Div(attrs = {
                ref { element ->
                    perspectiveRef.value = element.unsafeCast<HTMLElement>()
                    onDispose { perspectiveRef.value = null }
                }
            }) {
            if (selectedTab == ReviewTab.ISLAMIC) {
                Div {
                    concern.islamicPerspective?.let { perspective ->
                        Div(attrs = {
                            style {
                                fontWeight(700)
                                fontSize(14.px)
                                property("background", GradientPrimary)
                                property("-webkit-background-clip", "text")
                                property("-webkit-text-fill-color", "transparent")
                                property("background-clip", "text")
                                marginBottom(8.px)
                            }
                        }) { Text("Islamic Perspective") }
                        FormattedText(
                            text = perspective,
                            color = Gray600,
                            lineHeight = 1.7.em
                        )
                    }

                    concern.quranReference?.let { ref ->
                        QuranReferenceBox(reference = ref)
                    }

                    concern.hadithReference?.let { ref ->
                        HadithReferenceBox(reference = ref)
                    }
                }
            } else {
                concern.developmentalPerspective?.let { perspective ->
                    Div {
                        Div(attrs = {
                            style {
                                fontWeight(700)
                                fontSize(14.px)
                                color(DevelopmentalText)
                                marginBottom(8.px)
                            }
                        }) { Text("Child Development Perspective") }
                        FormattedText(
                            text = perspective,
                            color = Gray600,
                            lineHeight = 1.7.em
                        )
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun MiniTabPill(
    label: String,
    isSelected: Boolean,
    activeColor: CSSColorValue,
    onClick: () -> Unit
) {
    Div(attrs = {
        onClick { onClick() }
        style {
            property("flex", "1")
            property("text-align", "center")
            padding(6.px, 12.px)
            borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
            cursor("pointer")
            fontSize(12.px)
            fontWeight(if (isSelected) 700 else 500)
            property("transition", TransitionFast)
            if (isSelected) {
                backgroundColor(Color.white)
                color(activeColor)
                property("box-shadow", "0 1px 2px rgba(0,0,0,0.08)")
            } else {
                backgroundColor(Color.transparent)
                color(Gray500)
            }
        }
    }) { Text(label) }
}

@Composable
private fun AgeGuidanceSection(ageGuidance: String) {
    Div(attrs = {
        style {
            property("background", "linear-gradient(135deg, rgba(14, 124, 123, 0.08) 0%, rgba(20, 163, 160, 0.08) 100%)")
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            padding(20.px)
            marginBottom(20.px)
            border(1.px, LineStyle.Solid, rgba(14, 124, 123, 0.15))
        }
    }) {
        Div(attrs = {
            style {
                fontSize(12.px)
                fontWeight(700)
                color(PrimaryLight)
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
            }
        }) { Text("Age Guidance") }
        Div(attrs = {
            style {
                fontSize(20.px)
                fontWeight(700)
                marginTop(8.px)
                color(Primary)
                lineHeight(1.5.em)
            }
        }) { Text(ageGuidance) }
    }
}

@Composable
private fun PositiveAspectsSection(aspects: List<String>) {
    var isExpanded by remember { mutableStateOf(false) }

    Div(attrs = {
        onClick { isExpanded = !isExpanded }
        style {
            backgroundColor(rgb(239, 246, 255))
            borderRadius(RadiusMedium.unsafeCast<CSSNumeric>())
            padding(12.px)
            marginBottom(8.px)
            border(1.px, LineStyle.Solid, rgba(0, 0, 0, 0.08))
            cursor("pointer")
            property("transition", TransitionFast)
        }
    }) {
        // Header row: chevron + title + badge
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("justify-content", "space-between")
            }
        }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    property("flex", "1")
                }
            }) {
                Span(attrs = {
                    style {
                        fontSize(10.px)
                        color(Gray400)
                        property("transition", "transform 0.2s ease")
                        if (isExpanded) property("transform", "rotate(90deg)")
                    }
                }) { Text("\u25B6") }
                Span(attrs = {
                    style {
                        fontSize(16.px)
                        fontWeight(700)
                        color(rgb(30, 64, 175))
                    }
                }) { Text("Positive Aspects") }
            }
            Span(attrs = {
                style {
                    fontSize(10.px)
                    fontWeight(700)
                    color(rgb(30, 64, 175))
                    backgroundColor(rgb(219, 234, 254))
                    borderRadius(RadiusFull.unsafeCast<CSSNumeric>())
                    padding(3.px, 10.px)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.05em")
                    property("flex-shrink", "0")
                }
            }) { Text("Benefits") }
        }

        if (isExpanded) {
            Div(attrs = {
                style {
                    marginTop(10.px)
                    property("border-top", "1px solid rgba(0,0,0,0.06)")
                    paddingTop(10.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }) {
                aspects.forEach { aspect ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            alignItems(AlignItems.FlexStart)
                        }
                    }) {
                        Span(attrs = {
                            style {
                                fontSize(14.px)
                                property("flex-shrink", "0")
                                color(Gray400)
                                marginTop(2.px)
                            }
                        }) { Text("\u2022") }
                        Span(attrs = {
                            style {
                                fontSize(14.px)
                                color(Gray600)
                                lineHeight(1.7.em)
                            }
                        }) { Text(aspect) }
                    }
                }
            }
        }
    }
}


/** Split description text into bullet points. Splits on ". " followed by uppercase letter. */
private fun splitIntoBullets(text: String): List<String> {
    if (text.isBlank()) return emptyList()
    // Split on ". " followed by an uppercase letter
    val parts = text.split(Regex("(?<=\\.)\\s+(?=[A-Z])"))
    return parts.map { it.trim() }.filter { it.isNotEmpty() }
}
